import { io, Socket } from "socket.io-client";

/**
 * SOCKET.IO CLIENT MANAGER
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Connection state management
 * - Event type safety
 * - Error handling
 * - Heartbeat monitoring
 */

const API_URL = import.meta.env.VITE_WS_URL || "http://localhost:3000";

export type ConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "error";

export interface SocketEvents {
  // Server -> Client
  round_state: (data: RoundStateData) => void;
  round_started: (data: RoundStartedData) => void;
  round_running: (data: RoundRunningData) => void;
  multiplier_update: (data: MultiplierUpdateData) => void;
  round_crashed: (data: RoundCrashedData) => void;
  round_complete: (data: RoundCompleteData) => void;

  bet_placed: (data: BetPlacedData) => void;
  cashout_success: (data: CashoutSuccessData) => void;
  player_bet: (data: PlayerBetData) => void;
  player_cashed_out: (data: PlayerCashedOutData) => void;

  balance_update: (data: BalanceUpdateData) => void;
  history: (data: HistoryData) => void;
  error: (data: ErrorData) => void;

  // Connection events
  connect: () => void;
  disconnect: (reason: string) => void;
  connect_error: (error: Error) => void;
}

// Data interfaces
export interface RoundStateData {
  roundId: string;
  roundNumber: number;
  phase: string;
  commitment: string;
  currentMultiplier?: number;
  bettingStartTime?: number;
  startTime?: number;
}

export interface RoundStartedData {
  roundId: string;
  roundNumber: number;
  commitment: string;
  bettingDuration: number;
  bettingStartTime: number;
}

export interface RoundRunningData {
  roundId: string;
  startTime: number;
}

export interface MultiplierUpdateData {
  roundId: string;
  multiplier: number;
  elapsed: number;
}

export interface RoundCrashedData {
  roundId: string;
  crashPoint: number;
  crashTime: number;
}

export interface RoundCompleteData {
  roundId: string;
  crashPoint: number;
}

export interface BetPlacedData {
  betId: string;
  amount: number;
  balance: number;
  autoCashoutAt?: number;
}

export interface CashoutSuccessData {
  betId: string;
  payout: number;
  profit: number;
  multiplier: number;
  balance: number;
}

export interface PlayerBetData {
  username: string;
  amount: number;
  roundId: string;
}

export interface PlayerCashedOutData {
  username: string;
  multiplier: number;
  payout: number;
  auto?: boolean;
}

export interface BalanceUpdateData {
  balance: number;
}

export interface HistoryData {
  rounds: Array<{
    id: string;
    roundNumber: number;
    crashPointRevealed: number;
    createdAt: string;
  }>;
  bets: Array<{
    id: string;
    roundId: string;
    amount: number;
    status: string;
    cashedOutAt?: number;
    payout?: number;
    profit?: number;
    placedAt: string;
  }>;
}

export interface ErrorData {
  message: string;
}

export class SocketManager {
  private socket: Socket | null = null;
  // private token: string | null = null;  // Reserved for future authentication
  private connectionState: ConnectionState = "disconnected";
  private listeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;

  /**
   * Connects to the server with authentication token
   */
  connect(token: string): void {
    if (this.socket?.connected) {
      console.warn("[SocketManager] Already connected");
      return;
    }

    // this.token = token;  // Reserved for future use
    this.connectionState = "connecting";

    this.socket = io(API_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.setupEventHandlers();
  }

  /**
   * Disconnects from the server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionState = "disconnected";
    // this.token = null;  // Reserved for future use
    this.reconnectAttempts = 0;
  }

  /**
   * Sets up socket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.info("[SocketManager] Connected");
      this.connectionState = "connected";
      this.reconnectAttempts = 0;
      this.notifyListeners("connect");
    });

    this.socket.on("disconnect", (reason) => {
      console.warn("[SocketManager] Disconnected:", reason);
      this.connectionState = "disconnected";
      this.notifyListeners("disconnect", reason);
    });

    this.socket.on("connect_error", (error) => {
      console.error("[SocketManager] Connection error:", error);
      this.connectionState = "error";
      this.reconnectAttempts++;

      // Socket.IO automatically retries, but notify listeners
      this.notifyListeners("connect_error", error);
    });

    // Forward all game events to listeners
    const gameEvents: Array<keyof SocketEvents> = [
      "round_state",
      "round_started",
      "round_running",
      "multiplier_update",
      "round_crashed",
      "round_complete",
      "bet_placed",
      "cashout_success",
      "player_bet",
      "player_cashed_out",
      "balance_update",
      "history",
      "error",
    ];

    gameEvents.forEach((event) => {
      this.socket?.on(event, (...args: unknown[]) => {
        this.notifyListeners(event, ...args);
      });
    });
  }

  /**
   * Registers an event listener
   */
  on<K extends keyof SocketEvents>(
    event: K,
    callback: SocketEvents[K],
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback as (...args: unknown[]) => void);

    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Removes an event listener
   */
  off<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback as (...args: unknown[]) => void);
    }
  }

  /**
   * Notifies all listeners of an event
   */
  private notifyListeners(event: string, ...args: unknown[]): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`[SocketManager] Error in ${event} listener:`, error);
        }
      });
    }
  }

  /**
   * Emits an event to the server
   */
  emit<T = unknown>(event: string, data: T): void {
    if (!this.socket?.connected) {
      console.error("[SocketManager] Cannot emit, not connected");
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Places a bet
   */
  placeBet(amount: number, autoCashoutAt?: number): void {
    const nonce = this.generateNonce();
    const timestamp = Date.now();

    console.log("[SocketManager] Placing bet:", {
      amount,
      autoCashoutAt,
      nonce,
      timestamp,
    });

    this.emit("place_bet", {
      amount,
      autoCashoutAt,
      nonce,
      timestamp,
    });
  }

  /**
   * Requests cashout
   */
  cashout(betId: string): void {
    this.emit("cashout", {
      betId,
      nonce: this.generateNonce(),
      timestamp: Date.now(),
    });
  }

  /**
   * Requests history
   */
  getHistory(limit = 50): void {
    this.emit("get_history", { limit });
  }

  /**
   * Gets connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Checks if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Generates a unique UUID v4 nonce
   */
  private generateNonce(): string {
    // Generate UUID v4 format
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }
}

// Singleton instance
export const socketManager = new SocketManager();
